import { toQueryParams, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  CreateHandlingUnitType,
  HandlingUnitType,
  PaginationParams,
  PharosApiResponse,
  PharosPaginatedResponse,
  UpdateHandlingUnitType,
} from '@klareospace/pharos-lib-types';

// ─── Params ───────────────────────────────────────────────────────────────────

export type ListHandlingUnitTypesParams = PaginationParams;

export interface SearchHandlingUnitTypesParams extends PaginationParams {
  statuses?: string[];
}

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createHandlingUnitTypeApi(client: ApiClient): {
  archiveHandlingUnitType: (id: string) => Promise<HandlingUnitType>;
  createHandlingUnitType: (data: CreateHandlingUnitType) => Promise<HandlingUnitType>;
  listHandlingUnitTypes: (params: ListHandlingUnitTypesParams) => Promise<PharosPaginatedResponse<HandlingUnitType>>;
  searchHandlingUnitTypes: (
    params: SearchHandlingUnitTypesParams,
  ) => Promise<PharosPaginatedResponse<HandlingUnitType>>;
  updateHandlingUnitType: (id: string, data: UpdateHandlingUnitType) => Promise<HandlingUnitType>;
} {
  return {
    archiveHandlingUnitType: async (id: string): Promise<HandlingUnitType> => {
      const response = await client.execute<PharosApiResponse<HandlingUnitType>>(() =>
        client.instance.delete(`/handling-unit-types/${id}`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    createHandlingUnitType: async (data: CreateHandlingUnitType): Promise<HandlingUnitType> => {
      const response = await client.execute<PharosApiResponse<HandlingUnitType>>(() =>
        client.instance.post('/handling-unit-types', data),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    listHandlingUnitTypes: async (
      params: ListHandlingUnitTypesParams,
    ): Promise<PharosPaginatedResponse<HandlingUnitType>> => {
      const query = toQueryParams(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<HandlingUnitType>>>(() =>
        client.instance.get('/handling-unit-types', { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchHandlingUnitTypes: async (
      params: SearchHandlingUnitTypesParams,
    ): Promise<PharosPaginatedResponse<HandlingUnitType>> => {
      const query = toQueryParams({ page: params.page, pageSize: params.pageSize });
      const body: Record<string, unknown> = {};
      if (params.statuses && params.statuses.length > 0) {
        body.statuses = params.statuses;
      }
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<HandlingUnitType>>>(() =>
        client.instance.post('/handling-unit-types/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    updateHandlingUnitType: async (id: string, data: UpdateHandlingUnitType): Promise<HandlingUnitType> => {
      const response = await client.execute<PharosApiResponse<HandlingUnitType>>(() =>
        client.instance.patch(`/handling-unit-types/${id}`, data),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}
